import React, { useEffect, useState } from "react"
import { useNavigate, Link, useSearchParams } from "react-router-dom"
import { toast } from "sonner"
import { authAPI, getErrorMessage } from "../../utils/api"
import { Button } from "../../components/ui/button"
import { Input } from "../../components/ui/input"
import { Label } from "../../components/ui/label"
import { ArrowRight, Eye, EyeOff, Shield } from "lucide-react"

const ResetPassword = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get("token")

  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  })
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!token) {
      toast.error("Reset link is invalid or missing.")
      navigate("/auth/forgot-password")
    }
  }, [token, navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!token) return

    if (formData.password.length < 8) {
      toast.error("Password must be at least 8 characters")
      return
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error("Passwords do not match")
      return
    }

    setLoading(true)
    try {
      await authAPI.resetPasswordConfirm({
        token,
        new_password: formData.password,
      })
      toast.success(
        "Password reset successful. Please sign in with your new password."
      )
      navigate("/login")
    } catch (error) {
      toast.error(getErrorMessage(error, "Unable to reset password"))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center h-full bg-[#060638]">
      <div className="w-full overflow-hidden flex flex-col md:flex-row h-[90vh] my-auto gap-10 bg-transparent">
        {/* Left Panel - Login Image */}
        <div
          className="w-full md:w-[50%] flex flex-col justify-between relative overflow-hidden rounded-2xl"
          style={{
            backgroundImage: "url('/login.png')",
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
          }}
        >
        </div>

        {/* Right Panel - Form */}
        <div className="w-full md:w-[50%] p-4 md:p-8 lg:p-12 xl:p-[84px] flex flex-col justify-start">
          <div className="flex items-center justify-between w-full max-w-[630px] mb-8 md:mb-12 lg:mb-24">
            <img src="/images/logo.png" alt="Logo" className="h-8 md:h-10 lg:h-12 mr-auto" />
            <Link
              to="/register"
              className="text-white hover:underline items-center font-medium border border-white rounded-full px-3 md:px-4 py-1.5 md:py-2 justify-center text-sm md:text-base"
              data-testid="register-link"
            >
              Sign Up
            </Link>
          </div>

          <h1 className="text-2xl md:text-3xl lg:text-[44px] font-bold text-white mb-6 md:mb-10 lg:mb-14">
            Reset Password
          </h1>

          <p className="text-gray-200 mb-6 md:mb-8 text-sm md:text-base">
            Enter and confirm your new password to secure your account.
          </p>

          <form
            onSubmit={handleSubmit}
            className="flex flex-col gap-5 w-full max-w-[630px]"
            data-testid="reset-password-form"
          >
            <div>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Enter new password"
                  value={formData.password}
                  onChange={(e) =>
                    setFormData({ ...formData, password: e.target.value })
                  }
                  className="w-full h-12 !bg-white border-0 text-[16px] rounded-none px-4 pr-12 !text-gray-900 placeholder:text-gray-400 placeholder:text-[16px] focus:ring-2 focus:ring-green-500 focus:outline-none shadow-sm"
                  required
                  minLength={8}
                  data-testid="reset-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <div>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Re-enter new password"
                  value={formData.confirmPassword}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      confirmPassword: e.target.value,
                    })
                  }
                  className="w-full h-12 !bg-white border-0 text-[16px] rounded-none px-4 pr-12 !text-gray-900 placeholder:text-gray-400 placeholder:text-[16px] focus:ring-2 focus:ring-green-500 focus:outline-none shadow-sm"
                  required
                  minLength={8}
                  data-testid="reset-confirm-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="w-5 h-5" />
                  ) : (
                    <Eye className="w-5 h-5" />
                  )}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-14 bg-[#ff2d2d] hover:bg-[#7f1d1d] text-white font-semibold rounded-none shadow-xl flex items-center justify-center gap-2 transition-all duration-300 transition-ease-in-out mt-5"
              disabled={loading}
              data-testid="reset-password-submit"
            >
              {loading ? (
                <div className="loading-spinner"></div>
              ) : (
                <>
                  <ArrowRight className="w-6 h-6" />
                  <span>Update Password</span>
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 text-left">
            <p className="text-gray-200 text-sm md:text-base">
              Back to{" "}
              <Link
                to="/login"
                className="text-gray-200 hover:text-white hover:underline font-medium"
              >
                Sign In
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default ResetPassword
